import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useTranslation } from "react-i18next"

import { AdminBonus2Service } from "services/bonus/AdminBonus2Service"
import { BonusViolation, BonusViolationCount } from "services/bonus/type"

import { getDisplayName, getViolationTypeColor, groupViolations, violationCountsByType } from "../../helpers/bonus2.helpers"

import { ViolationRoundCard } from "./ViolationRoundCard"

interface ViolationsDashboardProps {
  tenant: string
}

export const ViolationsDashboard: React.FC<ViolationsDashboardProps> = ({ tenant }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [violations, setViolations] = useState<BonusViolation[]>([])
  const [counts, setCounts] = useState<BonusViolationCount[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [filter, setFilter] = useState<string>("all")
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const navigate = useNavigate()

  useEffect(() => {
    loadViolations()
  }, [tenant])

  const loadViolations = async () => {
    setLoading(true)
    const data = await AdminBonus2Service.getViolations(tenant)
    if (data) {
      setViolations(data.violations)
      setCounts(data.counts)
      setTotal(data.total)
    }
    setLoading(false)
  }

  const loadMore = async () => {
    setLoadingMore(true)
    const data = await AdminBonus2Service.getViolations(tenant, violations.length)
    if (data) {
      setViolations((prev) => [...prev, ...data.violations.filter((v) => !prev.some((p) => p.id === v.id))])
      setCounts(data.counts)
      setTotal(data.total)
    }
    setLoadingMore(false)
  }

  // Iteration-3 Fix 01 — soft-dismiss a violation, then drop it from the list optimistically.
  // The server fold is decremented too, so the group's count and the tabs stay honest without
  // a refetch; a group whose last bet is dismissed disappears.
  const handleDismiss = async (violationId: string) => {
    const dismissed = violations.find((v) => v.id === violationId)
    const ok = await AdminBonus2Service.dismissViolation(violationId, tenant)
    if (!ok) return
    setViolations((prev) => prev.filter((v) => v.id !== violationId))
    setTotal((prev) => Math.max(0, prev - 1))
    if (!dismissed) return
    const userId = dismissed.player_bonus?.user_id ?? ""
    setCounts((prev) =>
      prev
        .map((c) => (c.user_id === userId && c.violation_type === dismissed.violation_type ? { ...c, count: c.count - 1 } : c))
        .filter((c) => c.count > 0)
    )
  }

  const groups = groupViolations(violations, counts).filter((g) => filter === "all" || g.violationType === filter)
  const statsByType = violationCountsByType(counts)
  const violationTypes = Object.keys(statsByType)
  const shownRounds = violations.length

  const tabs = [
    { key: "all", label: t("bonus2.violations.all") as string, count: total },
    ...violationTypes.map((type) => ({ key: type, label: type, count: statsByType[type] }))
  ]

  if (loading) {
    return <div className="py-8 text-center text-gray-400">{t("bonus2.violations.loading") as string}</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`rounded px-4 py-2 text-sm transition-colors ${
              filter === tab.key ? "bg-purple-600 text-white" : "bg-[#1A1A2E] text-gray-300 hover:bg-[#2A2A3E]"
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* The log is paginated; the counts above are the tenant's real totals. Say so rather than
          let an operator read the rounds on screen as everything there is. */}
      {total > shownRounds && (
        <div className="flex items-center gap-3 text-sm text-gray-400">
          {t("bonus2.violations.showingOf", { shown: shownRounds, total }) as string}
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="rounded bg-[#1A1A2E] px-3 py-1 text-gray-300 hover:bg-[#2A2A3E] disabled:opacity-50"
          >
            {t("bonus2.violations.loadMore") as string}
          </button>
        </div>
      )}

      {groups.length === 0 ? (
        <div className="rounded-lg border border-gray-700 bg-[#1A1A2E] p-8 text-center">
          <div className="text-gray-400">
            {(filter === "all" ? t("bonus2.violations.noneFound") : t("bonus2.violations.noneFoundFiltered", { filter })) as string}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => {
            const isOpen = Boolean(expanded[group.key])
            const latest = group.rows[0]
            return (
              <div key={group.key} className="rounded-lg border border-gray-700 bg-[#1A1A2E] p-4 transition-colors hover:border-rose-500">
                <div className="mb-3 flex items-start justify-between gap-4">
                  <div>
                    <span className={`inline-block rounded px-3 py-1 text-sm text-white ${getViolationTypeColor(group.violationType)}`}>
                      {group.violationType}
                    </span>
                    <div className="mt-2 text-gray-400 text-sm">
                      {t("bonus2.violations.player") as string}{" "}
                      <button className="link link-primary" onClick={() => navigate(`/user/${group.userId}`)}>
                        {group.username || group.userId}
                      </button>
                    </div>
                    {latest && <div className="text-sm text-gray-400">{getDisplayName(latest.player_bonus.bonus.name)}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="rounded bg-rose-500/20 px-3 py-1 text-sm font-semibold text-rose-300">
                      {t("bonus2.violations.violatingRounds", { count: group.count }) as string}
                    </span>
                    <button
                      onClick={() => setExpanded((prev) => ({ ...prev, [group.key]: !isOpen }))}
                      className="rounded bg-gray-600 px-3 py-1 text-xs text-white transition-colors hover:bg-gray-700"
                      disabled={group.rows.length === 0}
                    >
                      {
                        (group.rows.length === 0
                          ? t("bonus2.violations.roundsNotOnPage")
                          : isOpen
                          ? t("bonus2.violations.hideRounds")
                          : t("bonus2.violations.showRounds", { count: group.rows.length })) as string
                      }
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="space-y-2 border-t border-gray-700 pt-3">
                    {group.rows.map((v) => (
                      <ViolationRoundCard key={v.id} violation={v} onDismiss={handleDismiss} />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
